import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  IconButton,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { ColorCardProps } from '../../types/color';
import { colorUtils } from '../../utils/colorUtils';

const ColorCard: React.FC<ColorCardProps> = ({
  color,
  onEdit,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit(color);
  };

  const handleDelete = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    onDelete(color);
  };

  const handleViewDetails = () => {
    handleMenuClose();
    setDetailsDialogOpen(true);
  };

  const handleCopyHex = async () => {
    handleMenuClose();
    try {
      await navigator.clipboard.writeText(color.hexadecimal);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy color:', err);
    }
  };

  const contrastColor = colorUtils.getContrastColor(color.hexadecimal);
  const rgb = colorUtils.hexToRgb(color.hexadecimal);
  const cmyk = rgb ? colorUtils.rgbToCmyk(rgb.r, rgb.g, rgb.b) : null;
  const hsl = colorUtils.hexToHsl(color.hexadecimal);

  return (
    <>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        {/* Color Preview Header */}
        <Box
          sx={{
            height: 120,
            backgroundColor: color.hexadecimal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: contrastColor,
            position: 'relative',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textShadow: contrastColor === '#FFFFFF' ? '1px 1px 2px rgba(0,0,0,0.5)' : '1px 1px 2px rgba(255,255,255,0.5)',
            }}
          >
            {color.name}
          </Typography>
          
          {/* Action Menu */}
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: contrastColor,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.2)',
              },
            }}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          {/* Color Information */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Hexadecimal
            </Typography>
            <Chip
              label={color.hexadecimal}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
          </Box>

          {/* RGB Display */}
          {rgb && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                RGB
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {colorUtils.formatRgb(rgb.r, rgb.g, rgb.b)}
              </Typography>
            </Box>
          )}

          {/* CMYK Display */}
          {color.cmyk && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                CMYK
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {color.cmyk}
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Edit Color">
              <IconButton size="small" onClick={handleEdit} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy Hex">
              <IconButton size="small" onClick={handleCopyHex}>
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Tooltip title="Delete Color">
            <IconButton size="small" onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyHex}>
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Hex</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Color</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the color "{color.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon />
          Color Details: {color.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                width: '100%',
                height: 100,
                backgroundColor: color.hexadecimal,
                borderRadius: 1,
                border: '1px solid #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: contrastColor,
                fontWeight: 'bold',
                mb: 2,
              }}
            >
              {color.name}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Hexadecimal
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {color.hexadecimal}
              </Typography>
            </Box>

            {rgb && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  RGB
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  rgb({rgb.r}, {rgb.g}, {rgb.b})
                </Typography>
              </Box>
            )}

            {cmyk && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  CMYK
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  cmyk({cmyk.c}%, {cmyk.m}%, {cmyk.y}%, {cmyk.k}%)
                </Typography>
              </Box>
            )}

            {hsl && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  HSL
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  hsl({hsl.h}°, {hsl.s}%, {hsl.l}%)
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Color ID
              </Typography>
              <Typography variant="body1">
                {color.id}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button onClick={handleEdit} variant="contained" startIcon={<EditIcon />}>
            Edit Color
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ColorCard;
